import { type FC, type ChangeEvent, memo, useRef, useState } from 'react';
import { Chip } from 'Chip';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';
import { Button } from 'Button';
import { Text } from 'Text';
import { twMerge } from 'tailwind-merge';
import { type FileInputProps, type FileValidationError, type VanillaFileInputProps } from './FileInput.types';

const VanillaFileInput: FC<VanillaFileInputProps> = memo(
    // oxlint-disable-next-line max-lines-per-function
    ({
        selectedFiles = [],
        onFileSelect,
        disabled,
        className,
        multiple = false,
        note,
        buttonText,
        accept,
        validators = [],
    }) => {
        const fileInputRef = useRef<HTMLInputElement>(null);
        const isDisabled = disabled || (selectedFiles.length === 1 && !multiple);
        const [validationErrors, setValidationErrors] = useState<FileValidationError[]>([]);

        const handleButtonClick = () => {
            fileInputRef.current?.click();
        };

        // This is a crude way to deduplicate files, but it covers most of the cases we need to care about.
        // This will prevent us from duplicating files in the event that a user re-selects the exact same using a multi-select.
        // For instance, after encountering validation error(s) with other file(s), the user re-selects a file that was previously selected and valid.
        // This will obviously miss files that have been copied and have different names and/or lastModified but are otherwise identical.
        // If we wanted to be absolutely rock-solid in our deduplication logic, we should be hashing file contents and comparing the hashes.
        // We'd need to make this code async in order to do that, and it just feels like overkill for now.
        const deduplicatedFiles = (oldFiles: File[], newFiles: File[]) => {
            const uniqueNewFiles = newFiles.filter(
                file =>
                    !oldFiles.some(
                        f => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
                    ),
            );
            return [...oldFiles, ...uniqueNewFiles];
        };

        const validateFiles = (files: File[]) => {
            const validFiles: File[] = [];
            const errors: FileValidationError[] = [];

            files.forEach(file => {
                const messages = validators.map(v => v(file)?.message || '').filter(Boolean);
                if (messages.length > 0) {
                    errors.push({ fileName: file.name, messages });
                } else {
                    validFiles.push(file);
                }
            });

            return { validFiles, errors };
        };

        const resetFileInputRef = () => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files || []);
            if (files.length > 0) {
                const candidates = multiple ? deduplicatedFiles(selectedFiles, files) : files;
                const { validFiles, errors } = validateFiles(candidates);
                setValidationErrors(errors);
                onFileSelect?.(validFiles);
                resetFileInputRef();
            }
        };

        const handleRemoveFile = (fileToRemove: File) => {
            const updatedFiles = selectedFiles.filter(file => file !== fileToRemove);
            onFileSelect?.(updatedFiles);
            resetFileInputRef();
        };

        return (
            <div className={twMerge('flex flex-col gap-4', className)}>
                <input
                    accept={accept}
                    className="!hidden"
                    data-testid="file-input"
                    disabled={isDisabled}
                    multiple={multiple}
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    type="file"
                />

                <Button
                    variant="solid"
                    color="black"
                    onPress={handleButtonClick}
                    isDisabled={isDisabled}
                    height={9}
                    className="w-fit px-3"
                >
                    {buttonText}
                </Button>

                {selectedFiles.length > 0 && (
                    <div className="mt-2 flex flex-col flex-wrap gap-2">
                        {selectedFiles.map(file => (
                            <Chip
                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                onClose={() => handleRemoveFile(file)}
                            >
                                {file.name}
                            </Chip>
                        ))}
                    </div>
                )}

                {validationErrors.length > 0 && (
                    <div className="mt-1 flex flex-col gap-2">
                        {validationErrors.map(({ fileName, messages }) => (
                            <div key={`${fileName}`}>
                                {messages.map(message => (
                                    <Text
                                        key={`${fileName}-${message}`}
                                        color="red"
                                        className="text-base leading-[21px]"
                                    >
                                        {multiple ? `${fileName}: ${message}` : message}
                                    </Text>
                                ))}
                            </div>
                        ))}
                    </div>
                )}

                {note && (
                    <Text color="slate-grey" className="text-base leading-[21px]">
                        {note}
                    </Text>
                )}
            </div>
        );
    },
);

export const FileInput: FC<FileInputProps> = memo(({ name, ...fileInputProps }) => {
    const { control } = useFormContext();

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <VanillaFileInput {...fileInputProps} selectedFiles={field.value || []} onFileSelect={field.onChange} />
            )}
        />
    );
});
